import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import RouteFallback from "@/components/RouteFallback";
import { isChunkLoadError } from "@/lib/chunkLoadError";

/**
 * Error boundary around the lazily loaded routes.
 *
 * Its main job is the stale-chunk case: after a deploy, a client that still
 * runs the previous build asks for `assets/commandes-<oldhash>.js`, which no
 * longer exists, and `import()` rejects. The fix is simply to reload so the
 * browser fetches the new `index.html` and chunk names. The reload is done at
 * most once per minute per tab (sessionStorage guard) so a genuinely broken
 * deploy shows a message instead of a reload loop.
 *
 * Any other render error caught here shows a small recovery panel instead of
 * unmounting the whole app; React still reports it through `onCaughtError`
 * (Sentry) in main.tsx.
 */

const RELOAD_GUARD_KEY = "odilon:lazy-chunk-reload";
const RELOAD_GUARD_TTL_MS = 60_000;

/** Reloads the page unless a guarded reload already happened recently. */
function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_TTL_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // No sessionStorage (private mode quota, disabled storage): without a
    // guard an automatic reload could loop, so fall back to the manual panel.
    return false;
  }
  window.location.reload();
  return true;
}

function reloadManually() {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore
  }
  window.location.reload();
}

interface BoundaryProps {
  children: ReactNode;
  /** When this changes (route navigation) a previous error is cleared. */
  resetKey: string;
}

interface BoundaryState {
  error: Error | null;
  reloading: boolean;
}

class LazyRouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadError(error) && reloadOnce()) {
      this.setState({ reloading: true });
    }
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, reloading: false });
    }
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (reloading) return <RouteFallback />;

    const stale = isChunkLoadError(error);
    return (
      <div
        className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-16 text-center"
        role="alert"
      >
        <h1 className="text-lg font-semibold">Impossible d’afficher cette page</h1>
        <p className="text-sm text-muted-foreground">
          {stale
            ? "Une nouvelle version de l’application est disponible. Rechargez la page pour continuer."
            : "Une erreur inattendue est survenue. Rechargez la page ou revenez à l’accueil."}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={reloadManually}>Recharger la page</Button>
          <Button variant="outline" asChild>
            <Link to="/">Retour à l’accueil</Link>
          </Button>
        </div>
      </div>
    );
  }
}

/** Wraps the class boundary so it resets whenever the pathname changes. */
export default function LazyRouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <LazyRouteErrorBoundary resetKey={pathname}>{children}</LazyRouteErrorBoundary>
  );
}
